import React, { useState, useEffect, useContext } from "react";

import { CoreContext } from "../../../context/global/CoreContext";
import { TimekeepingContext } from "../../../context/domain/timekeeping/TimekeepingContext";

import { isDefaultLanguage, UI_COPY, uiText, clockInStageText } from "../../../config";
import { useUiText } from "../../../hooks/useUiText";
import { clockInUser } from "../../../services/api/timekeeping";
import { getJobNumbers } from "../../../services/api/core";
import WorkDescriptionTranslation from "./components/WorkDescriptionTranslation";

import {
  findClockedInShifts,
  getCurrentTimeInWindow,
  addHoursTo24TimeString,
} from "../../../utils/datetime";

// Import icons
import { Clock } from "../../../components/icons/svg/outline/index";
import { Button } from "../../../components/ui";
import StatusMessage from "../../../components/common/StatusMessage";

/**
 * ClockIn Component
 *
 * Provides functionality for users to clock in for their work shifts.
 *
 * Features:
 * - Loads user's scheduled job and work scope for the current date
 * - Allows selection of job, work scope, and adding work description
 * - Sets default time to current time, but allows manual adjustment
 * - Handles form validation and submission to clock in API
 * - Displays status messages for user feedback
 * - Animates transition after successful clock in
 *
 * Context Usage:
 * - CoreContext: Retrieves user info and available jobs list
 * - TimekeepingContext: Manages clock in/out state, shift data, and loading states
 *
 * @returns {JSX.Element} The ClockIn component UI
 */
const ClockIn = () => {
  const { user, scopesList } = useContext(CoreContext);
  const { language, t, formatUiDate } = useUiText();
  const {
    date,
    setDate,
    setShowClockOutContent,
    isLoading,
    setIsLoading,
    weeklyShiftData,
    fetchWeeklyShifts,
    dailyShiftData,
    // Use message state from context
    message,
    setMessageDebounced,
  } = useContext(TimekeepingContext); // State management
  const [shiftData, setShiftData] = useState({
    job: "",
    workScope: "",
    workDescription: "",
    startTime: "",
  });
  const [isAnimating, setIsAnimating] = useState(false);
  // Fetch jobs for the specific date (not just today)
  const [jobsList, setJobsList] = useState([]);

  // Fetch jobs for the current date whenever date changes
  useEffect(() => {
    /**
     * Load jobs active on the current timekeeping `date`.
     *
     * @returns {Promise<void>}
     */
    const fetchJobsForDate = async () => {
      try {
        const jobs = await getJobNumbers(true, date);
        setJobsList(jobs);
      } catch (error) {
        console.error('Error fetching jobs for date:', error);
        setJobsList([]);
      }
    };
    fetchJobsForDate();
  }, [date]);
  // Check the weekly shift data to see if there are any open clock in events
  useEffect(() => {
    if (weeklyShiftData) {
      const clockedInShifts = findClockedInShifts(weeklyShiftData);

      if (clockedInShifts.length > 0) {
        // Use the first clocked-in shift found
        const firstClockedIn = clockedInShifts[0];

        // Update shift data with the found shift
        setShiftData(firstClockedIn.shift);
        setDate(firstClockedIn.date);

        // Notify user
        setMessageDebounced({
          type: "info",
          text: uiText(UI_COPY.clockIn.alreadyClockedIn, language, {
            date: formatUiDate(firstClockedIn.date, "month DD, YYYY"),
          }),
        });
      }
    }
  }, [weeklyShiftData, date, setMessageDebounced, setDate, language, formatUiDate]);

  // Handle field changes
  /**
   * Update one clock-in form field.
   *
   * @param {string} field Field key (`job`, `workScope`, `workDescription`, `startTime`).
   * @param {*} value Next value.
   * @returns {void}
   */
  const handleFieldChange = (field, value) => {
    setShiftData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Format today's date for user display
  const formattedDate = formatUiDate(date, "day month DD, YYYY");
  // Set default time to current time from client device
  useEffect(() => {
    const now = new Date(); // Creates a date object with the client's local time
    // Format time in 24-hour format (HH:MM)
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    handleFieldChange("startTime", formattedTime);
  }, [setIsLoading]);

  // Get scheduled shift from context shift data, if it exists, to pre-fill form
  useEffect(() => {
    setIsLoading(true);
    setMessageDebounced({
      type: "info",
      topic: "schedule",
      text: uiText(UI_COPY.clockIn.lookingForSchedule, language, {
        date: formattedDate,
      }),
    });
    if (dailyShiftData && dailyShiftData.length > 0) {
      const currentShift = dailyShiftData.find(
        (shift) =>
          shift.stage === "Current Shift" || shift.stage === "Missed Shift"
      );
      if (currentShift) {
        // Calculate the appropriate start time based on current time window
        const calculatedStartTime = getCurrentTimeInWindow(
          currentShift.startTime,
          addHoursTo24TimeString(currentShift.startTime, 2),
          currentShift.startTime
        );
        // Update shift data with calculated start time to match what will be displayed
        setShiftData({
          ...currentShift,
          startTime: calculatedStartTime,
        });
        setMessageDebounced({
          type: "success",
          topic: "schedule",
          text: uiText(UI_COPY.clockIn.scheduleLoaded, language, {
            date: formattedDate,
          }),
        });
      } else {
        setMessageDebounced({
          type: "warning",
          topic: "schedule",
          text: uiText(
            dailyShiftData.length > 1
              ? UI_COPY.clockIn.additionalShiftsNotFound
              : UI_COPY.clockIn.scheduleNotFound,
            language,
            { date: formattedDate }
          ),
        });
        setIsLoading(false);
      }
    }
  }, [dailyShiftData, formattedDate, setIsLoading, setMessageDebounced, language]);

  /**
   * Validate required fields and start time, then call `clockInUser`.
   *
   * @returns {Promise<void>}
   */
  const handleClockIn = async () => {
    setIsLoading(true);
    if (
      !shiftData.job ||
      !shiftData.workScope ||
      !shiftData.startTime ||
      shiftData.job === "" ||
      shiftData.workScope === ""
    ) {
      setMessageDebounced({ type: "error", text: t(UI_COPY.clockIn.fillAllFields) });
      setIsLoading(false);
      return;
    }

    // Validate start time against daily shift data (cannot clock in before last shift's finish time)
    if (
      dailyShiftData &&
      dailyShiftData.length > 0 &&
      shiftData.startTime <
        dailyShiftData[dailyShiftData.length - 1]?.finishTime
    ) {
      setMessageDebounced({
        type: "error",
        text: t(UI_COPY.clockIn.timeInBeforePrevious),
      });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const result = await clockInUser({
        job: shiftData.job,
        date,
        workScope: shiftData.workScope,
        workDescription: shiftData.workDescription,
        startTime: shiftData.startTime,
        userId: user?.id,
      });
      if (result.success) {
        setMessageDebounced({
          type: "success",
          text: t(UI_COPY.clockIn.clockInSuccessful),
        });
        setTimeout(() => {
          setMessageDebounced(null); // Clear message after 2 seconds
        }, 1000);
        // Start animation
        setIsAnimating(true); // Update context to show user is clocked in after animation
        setTimeout(() => {
          fetchWeeklyShifts(); // Fetch weekly shifts to update state
          setTimeout(() => {
            // Allow time for clock out components to load before triggering animations
            setShowClockOutContent(true);
          }, 50);
        }, 1000);
      } else {
        setMessageDebounced({
          type: "error",
          text: result.message || t(UI_COPY.clockIn.clockInFailed),
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error clocking in:", error);
      setMessageDebounced({
        type: "error",
        text: t(UI_COPY.clockIn.clockInError, { error: error.message }),
      });
      setIsLoading(false);
    }
  };

  // Allow all messages to display for 0.5 seconds before allowing interaction.
  // This is purely for UX purposes to ensure users see the message before they can interact with the form again.
  setTimeout(() => {
    setIsLoading(false);
  }, 500);

  return (
    <div className="CLOCK-IN">
      {/* Clock In Form */}
      <div
        className={`
        ${isAnimating ? "animate-shrink-up bg-yellow-50" : "bg-gray-100"} 
        p-2 rounded-md
        transition-all duration-1000 ease-in`}
      >
        <div
          className={`${isAnimating ? "opacity-0" : "opacity-100"} transition-opacity ease-in duration-1000`}
        >
          <h2 className="flex items-center text-lg text-black font-medium mb-4">
            <Clock className="text-gray-700 mr-2" />
            {t(UI_COPY.clockIn.title)}{" "}
            {shiftData.stage
              ? `(${clockInStageText(shiftData.stage, language)})`
              : `(${t(UI_COPY.clockIn.stages["New Shift"])})`}
          </h2>{" "}
          {message && <StatusMessage message={message} className="mb-4" />}
          {/* Job Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(UI_COPY.clockIn.job)}
            </label>
            <div className="relative">
              {" "}
              <select
                value={shiftData.job}
                onChange={(e) => handleFieldChange("job", e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              >
                <option value="">{t(UI_COPY.clockIn.selectJob)}</option>
                {jobsList.length > 0 ? (
                  jobsList.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.number} - {job.description}
                    </option>
                  ))
                ) : (
                  <option value="">{t(UI_COPY.clockIn.noJobs)}</option>
                )}
              </select>
            </div>
          </div>
          {/* Work Scope */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(UI_COPY.clockIn.workScope)}
            </label>
            <div className="relative">
              {" "}
              <select
                value={shiftData.workScope}
                onChange={(e) => handleFieldChange("workScope", e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t(UI_COPY.clockIn.selectScope)}</option>
                {scopesList.length > 0 ? (
                  scopesList.map((scope) => (
                    <option key={scope.id} value={scope.id}>
                      {scope.title}
                    </option>
                  ))
                ) : (
                  <option value="">{t(UI_COPY.clockIn.noScopes)}</option>
                )}
              </select>
            </div>
          </div>
          {/* Work Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(UI_COPY.clockIn.workDescription)}
              {!isDefaultLanguage(language) ? (
                <span className="ml-1 font-normal text-gray-500">
                  {t(UI_COPY.clockIn.workDescriptionHint)}
                </span>
              ) : null}
            </label>{" "}
            <textarea
              value={shiftData.workDescription}
              onChange={(e) =>
                handleFieldChange("workDescription", e.target.value)
              }
              placeholder={t(UI_COPY.clockIn.workDescriptionPlaceholder)}
              rows={5}
              className="block caret-black w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            ></textarea>
            <WorkDescriptionTranslation
              text={shiftData.workDescription}
            />
          </div>
          {/* Time In */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(UI_COPY.clockIn.timeIn)}
            </label>{" "}
            <input
              type="time"
              value={shiftData.startTime || ""}
              onChange={(e) => handleFieldChange("startTime", e.target.value)}
              className="block w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>{" "}
          {/* Clock In Button */}
          <Button
            onClick={handleClockIn}
            variant={isLoading ? "disabled" : "secondary"}
            text={isLoading ? "" : t(UI_COPY.clockIn.submit)}
            icon={isLoading ? "spinner" : null}
            className="py-3 px-6 w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default ClockIn;
